import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function VinylPlayer({
  audioSrc,
  coverImage,
  songTitle = 'Our Song',
  artistName = 'Wedding Soundtrack',
  accentColor = '#2c241d',
  bgColor = '#f8f3eb',
  isPreview = false,
}) {
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const hasAutoPlayed = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isOutOfView, setIsOutOfView] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const progressPercent = useMemo(() => {
    if (!duration) return 0;
    return (currentTime / duration) * 100;
  }, [currentTime, duration]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      setIsReady(true);
    };
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(audio.duration || 0); };
    const onPause = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    };
  }, []);

  // Auto-play on first user interaction (scroll, tap, click)
  useEffect(() => {
    if (isPreview || !audioSrc) return;

    const autoStart = () => {
      if (hasAutoPlayed.current) return;
      hasAutoPlayed.current = true;
      const audio = audioRef.current;
      if (!audio || !audio.paused) return;
      audio.volume = 0;
      audio.play().then(() => {
        let vol = 0;
        fadeIntervalRef.current = setInterval(() => {
          vol = Math.min(vol + 0.04, 1);
          audio.volume = vol;
          if (vol >= 1) clearInterval(fadeIntervalRef.current);
        }, 100);
      }).catch(() => {});
      document.removeEventListener('click', autoStart);
      document.removeEventListener('touchstart', autoStart);
      document.removeEventListener('scroll', autoStart);
    };

    document.addEventListener('click', autoStart, { once: true });
    document.addEventListener('touchstart', autoStart, { once: true });
    document.addEventListener('scroll', autoStart, { once: true });

    return () => {
      document.removeEventListener('click', autoStart);
      document.removeEventListener('touchstart', autoStart);
      document.removeEventListener('scroll', autoStart);
    };
  }, [isPreview, audioSrc]);

  // IntersectionObserver for floating mini-player
  useEffect(() => {
    if (isPreview) return;
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsOutOfView(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isPreview]);

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (audio.paused) {
        audio.volume = 1;
        await audio.play();
      } else {
        audio.pause();
      }
    } catch (error) {
      console.error('Audio playback failed:', error);
    }
  };

  const handleSeek = (value) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const nextTime = (value / 100) * duration;
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const showFloating = !isPreview && isOutOfView && (isPlaying || !dismissed);

  const DISC_SIZE = 187;

  return (
    <>
      <div ref={containerRef} className="w-full px-4 py-8 relative" style={{ backgroundColor: bgColor }}>
        {/* Vinyl disc + tonearm */}
        <div className="flex flex-col items-center">
          <div className="relative" style={{ width: `${DISC_SIZE}px`, height: `${DISC_SIZE}px` }}>
            {/* Shadow */}
            <div className="absolute inset-2 rounded-full bg-black/10 blur-xl" />

            {/* PNG Tonearm — pivots when playing */}
            <motion.div
              animate={{
                rotate: isPlaying ? 16 : 0,
              }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              className="absolute z-30"
              style={{
                right: '-6%',
                top: '-4%',
                width: '46px',
                height: '130px',
                transformOrigin: '70% 12%',
              }}
            >
              <img
                src="/assets/tonearm.png"
                alt="Tonearm"
                className="w-full h-full"
                style={{ objectFit: 'contain' }}
                draggable={false}
              />
            </motion.div>

            {/* Spinning vinyl disc (CSS-drawn) */}
            <motion.div
              animate={{ rotate: isPlaying ? 360 : 0 }}
              transition={{
                duration: 4,
                ease: 'linear',
                repeat: isPlaying ? Infinity : 0,
              }}
              className="absolute inset-0 z-20 rounded-full"
            >
              <div
                className="relative h-full w-full rounded-full"
                style={{
                  background: 'radial-gradient(circle at center, #2f2f2f 0%, #171717 52%, #0d0d0d 72%, #000 100%)',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.3)',
                }}
              >
                {/* Grooves */}
                {[7, 12, 17, 22, 27, 32, 37].map((inset) => (
                  <div
                    key={inset}
                    className="absolute rounded-full border border-white/10"
                    style={{ inset: `${inset}%` }}
                  />
                ))}

                {/* Highlight reflection */}
                <div
                  className="absolute rounded-full"
                  style={{
                    inset: '8%',
                    background: 'conic-gradient(from 210deg, rgba(255,255,255,0.12), transparent 25%, transparent 75%, rgba(255,255,255,0.07))',
                  }}
                />

                {/* Center label with cover image */}
                <div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20 shadow-inner"
                  style={{
                    width: '42%',
                    height: '42%',
                    backgroundColor: '#efe5d6',
                  }}
                >
                  {coverImage && (
                    <div
                      className="absolute rounded-full bg-cover bg-center"
                      style={{
                        inset: '10%',
                        backgroundImage: `url('${coverImage}')`,
                      }}
                    />
                  )}
                  <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/40 bg-[#111]" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Song info + controls */}
          <div className="mt-6 w-full max-w-[280px] text-center">
            <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: `${accentColor}80` }}>
              Now Playing
            </p>
            <h3 className="mt-1 text-lg font-semibold tracking-tight" style={{ color: accentColor }}>
              {songTitle}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: `${accentColor}99` }}>
              {artistName}
            </p>

            {/* Play button + progress */}
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={togglePlayback}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-md transition hover:scale-105 active:scale-95"
                style={{ backgroundColor: accentColor }}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                data-testid="vinyl-play-btn"
              >
                {isPlaying ? (
                  <span className="flex gap-0.5">
                    <span className="block h-3.5 w-[3px] rounded bg-white" />
                    <span className="block h-3.5 w-[3px] rounded bg-white" />
                  </span>
                ) : (
                  <span
                    className="ml-0.5 block h-0 w-0"
                    style={{
                      borderTop: '7px solid transparent',
                      borderBottom: '7px solid transparent',
                      borderLeft: '11px solid white',
                    }}
                  />
                )}
              </button>

              <div className="min-w-0 flex-1">
                <div className="mb-1 flex justify-between text-[10px]" style={{ color: `${accentColor}80` }}>
                  <span>{formatTime(currentTime)}</span>
                  <span>{isReady ? formatTime(duration) : '--:--'}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={0.1}
                  value={progressPercent}
                  onChange={(e) => handleSeek(Number(e.target.value))}
                  className="h-1.5 w-full cursor-pointer appearance-none rounded-full"
                  style={{
                    background: `linear-gradient(to right, ${accentColor} ${progressPercent}%, #ddd1bf ${progressPercent}%)`,
                  }}
                  aria-label="Seek"
                />
              </div>
            </div>
          </div>
        </div>

        <audio ref={audioRef} preload="metadata">
          <source src={audioSrc} type="audio/mpeg" />
        </audio>
      </div>

      {/* Floating mini-player */}
      <AnimatePresence>
        {showFloating && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-full shadow-xl border border-white/20"
            style={{
              backgroundColor: accentColor,
              maxWidth: '320px',
              backdropFilter: 'blur(12px)',
            }}
            data-testid="vinyl-floating-player"
          >
            <motion.div
              animate={{ rotate: isPlaying ? 360 : 0 }}
              transition={{ duration: 3, ease: 'linear', repeat: isPlaying ? Infinity : 0 }}
              className="w-8 h-8 rounded-full flex-shrink-0 border border-white/30"
              style={{
                background: 'radial-gradient(circle, #333 40%, #111 100%)',
              }}
            >
              <div className="w-full h-full rounded-full flex items-center justify-center">
                <div className="w-2.5 h-2.5 rounded-full bg-white/80" />
              </div>
            </motion.div>

            <div className="min-w-0 flex-1">
              <p className="text-white text-xs font-medium truncate">{songTitle}</p>
              <p className="text-white/60 text-[10px] truncate">{artistName}</p>
            </div>

            <button
              type="button"
              onClick={togglePlayback}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-white/20 hover:bg-white/30 transition flex-shrink-0"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <span className="flex gap-0.5">
                  <span className="block h-3 w-[2.5px] rounded bg-white" />
                  <span className="block h-3 w-[2.5px] rounded bg-white" />
                </span>
              ) : (
                <span
                  className="ml-0.5 block h-0 w-0"
                  style={{
                    borderTop: '5px solid transparent',
                    borderBottom: '5px solid transparent',
                    borderLeft: '8px solid white',
                  }}
                />
              )}
            </button>

            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="w-6 h-6 rounded-full flex items-center justify-center text-white/50 hover:text-white/80 transition flex-shrink-0"
              aria-label="Dismiss"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M1 1l8 8M9 1l-8 8" />
              </svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
